/**
 * Public sponsor rate card. Every zone, what it costs flat and on CPM, how the
 * interstitial behaves, and a live preview of each unit rendered with sample
 * creative — so a sponsor knows exactly what they are buying before signing in.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents, type AdZoneDef } from '@fairwaylive/shared';
import { AdProvider } from '../lib/adClient.tsx';
import { AdZone } from '../components/AdZone.tsx';
import { ErrorNote, Section, Spinner } from '../components/Shell.tsx';
import { useFetch } from '../lib/useLive.ts';

interface ZonesResponse {
  zones: Array<AdZoneDef & { exampleCpmSpendCents: number }>;
  currency: string;
  timing: {
    popupIntervalSeconds: number;
    popupDurationSeconds: number;
    popupSkipAfterSeconds: number;
    staticRotateSeconds: number;
    bigscreenRotateSeconds: number;
  };
}

interface PackagesResponse {
  source: string;
  packages: Array<{
    key: string;
    name: string;
    zoneKey: string;
    pricingModel: string;
    flatPriceCents: number | null;
    cpmCents: number | null;
    impressionsIncluded: number | null;
    durationDays: number;
    isExclusive: boolean;
    description: string;
  }>;
}

export function RateCardPage() {
  const zones = useFetch<ZonesResponse>('/api/catalogue/ad-zones');
  const packages = useFetch<PackagesResponse>('/api/catalogue/ad-packages');
  const [impressions, setImpressions] = useState(50_000);

  if (zones.loading) return <Spinner label="Loading rate card" />;
  if (zones.error || !zones.data) {
    return (
      <div className="mx-auto max-w-[820px] px-4 py-16">
        <ErrorNote message={zones.error ?? 'Rate card unavailable'} />
      </div>
    );
  }

  const { timing } = zones.data;

  return (
    <AdProvider surface="landing" isDemo allowInterstitial={false}>
      <Section eyebrow="Sponsor rate card" title="Nine placements. Buy them yourself, in minutes.">
        <p className="-mt-2 max-w-[68ch] text-[15px] text-[var(--color-muted)]">
          Prices are indicative list rates in {zones.data.currency}, before agency or multi-event discounts. Every
          placement is bought self-serve in the sponsor portal: choose a package, pay, upload creative, watch delivery.
          Exclusive inventory is refused rather than double-booked.
        </p>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[880px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_28%,transparent)] text-left text-[var(--color-muted)]">
                <th className="py-3">Placement</th>
                <th className="py-3">Size</th>
                <th className="py-3">Where it runs</th>
                <th className="py-3 text-right">Flat / month</th>
                <th className="py-3 text-right">CPM</th>
                <th className="py-3 text-right">At {impressions.toLocaleString('en-ZA')} impressions</th>
              </tr>
            </thead>
            <tbody>
              {zones.data.zones.map((zone) => (
                <tr key={zone.key} className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)] align-top">
                  <td className="py-3 pr-4">
                    <p className="font-semibold">{zone.label}</p>
                    <p className="max-w-[42ch] text-[11px] leading-relaxed text-[var(--color-muted-dim)]">{zone.description}</p>
                  </td>
                  <td className="tabular py-3 text-[13px] text-[var(--color-muted)]">
                    {zone.size.w}×{zone.size.h}
                    <span className="block text-[10px] text-[var(--color-muted-dim)]">
                      mobile: {zone.mobile}
                    </span>
                  </td>
                  <td className="py-3 text-[12px] text-[var(--color-muted)]">{zone.surfaces.join(', ')}</td>
                  <td className="tabular py-3 text-right font-semibold">
                    {zone.indicativeFlatCentsMonthly ? formatCents(zone.indicativeFlatCentsMonthly) : <span className="text-[var(--color-muted-dim)]">on request</span>}
                  </td>
                  <td className="tabular py-3 text-right text-[var(--color-muted)]">
                    {zone.indicativeCpmCents ? formatCents(zone.indicativeCpmCents) : '—'}
                  </td>
                  <td className="tabular py-3 text-right font-semibold text-[var(--color-gold-400)]">
                    {zone.indicativeCpmCents ? formatCents(Math.round((impressions / 1000) * zone.indicativeCpmCents)) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-3 text-sm text-[var(--color-muted)]">
            Model volume
            <input
              type="range"
              min={5000}
              max={500_000}
              step={5000}
              value={impressions}
              onChange={(event) => setImpressions(Number(event.target.value))}
              className="w-[240px] accent-[var(--color-fairway-300)]"
            />
            <span className="tabular font-semibold text-[var(--color-chalk)]">{impressions.toLocaleString('en-ZA')}</span>
          </label>
          <Link to="/sponsor" className="btn btn-gold">
            Buy a placement
          </Link>
        </div>
      </Section>

      <Section eyebrow="How the units behave" title="No dark patterns, and the numbers hold up">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Interstitial cadence" value={`every ${timing.popupIntervalSeconds / 60} min`} note="Configurable per event, never on arrival." />
          <Fact label="Interstitial duration" value={`${timing.popupDurationSeconds}s`} note={`Skip button unlocks after ${timing.popupSkipAfterSeconds}s.`} />
          <Fact label="Static rotation" value={`${timing.staticRotateSeconds}s`} note="Multiple sponsors share a zone by weight." />
          <Fact label="Big screen rotation" value={`${timing.bigscreenRotateSeconds}s`} note="Full-bleed card in every board cycle." />
          <Fact label="Impressions counted" value="On view, not on load" note="50% of the unit visible for 400ms, deduped per session." />
          <Fact label="Clicks" value="Server-counted" note="Redirected through a signed endpoint, so nothing is inflated." />
          <Fact label="Reporting" value="Live" note="Impressions, clicks, CTR, delivery pacing and effective CPM." />
          <Fact label="Demo traffic" value="Never billed" note="Sample creative on /demo is counted in an isolated sink." />
        </div>
      </Section>

      <Section id="zones" eyebrow="Live previews" title="What each unit actually looks like">
        <p className="-mt-2 mb-6 max-w-[62ch] text-sm text-[var(--color-muted)]">
          Rendered below with sample creative for fictional sponsors, at real dimensions.
        </p>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-5">
            <PreviewBlock title="Top banner — 970×90">
              <AdZone zoneKey="top_banner" hideWhenEmpty={false} />
            </PreviewBlock>
            <PreviewBlock title="In-feed row — 728×90, injected every 8 leaderboard rows">
              <AdZone zoneKey="inline_leaderboard" hideWhenEmpty={false} />
            </PreviewBlock>
            <PreviewBlock title="Sticky footer — 728×90, travels with the scorer">
              <AdZone zoneKey="footer_banner" hideWhenEmpty={false} />
            </PreviewBlock>
            <PreviewBlock title="Marketplace / affiliate slot — 300×300">
              <AdZone zoneKey="affiliate_slot" hideWhenEmpty={false} className="max-w-[320px]" />
            </PreviewBlock>
          </div>
          <div className="space-y-5">
            <PreviewBlock title="Primary sidebar — 300×600">
              <AdZone zoneKey="sidebar_primary" hideWhenEmpty={false} />
            </PreviewBlock>
            <PreviewBlock title="Secondary sidebar — 300×250">
              <AdZone zoneKey="sidebar_secondary" hideWhenEmpty={false} />
            </PreviewBlock>
          </div>
        </div>
        <p className="mt-6 text-sm text-[var(--color-muted)]">
          The interstitial takeover and the clubhouse big-screen units are best seen in place —{' '}
          <Link to="/demo" className="text-[var(--color-fairway-300)] underline">
            open the live demo
          </Link>{' '}
          and wait two minutes, or{' '}
          <Link to="/demo/bigscreen" className="text-[var(--color-fairway-300)] underline">
            cast the clubhouse display
          </Link>
          .
        </p>
      </Section>

      {packages.data ? (
        <Section eyebrow="Packages" title="Buy-it-now inventory">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packages.data.packages.map((pkg) => (
              <article key={pkg.key} className="card flex flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-semibold">{pkg.name}</h3>
                  {pkg.isExclusive ? (
                    <span className="whitespace-nowrap rounded bg-[var(--color-gold-500)] px-2 py-0.5 text-[10px] font-bold uppercase text-[#1a1204]">
                      Exclusive
                    </span>
                  ) : null}
                </div>
                <p className="tabular mt-2 text-xl font-bold text-[var(--color-gold-400)]">
                  {pkg.pricingModel === 'flat'
                    ? formatCents(pkg.flatPriceCents)
                    : `${formatCents(Math.round(((pkg.impressionsIncluded ?? 0) / 1000) * (pkg.cpmCents ?? 0)))}`}
                </p>
                <p className="text-[11px] text-[var(--color-muted-dim)]">
                  {pkg.pricingModel === 'flat'
                    ? `${pkg.durationDays} days, unlimited impressions`
                    : `${pkg.impressionsIncluded?.toLocaleString('en-ZA')} impressions at ${formatCents(pkg.cpmCents)} CPM`}
                </p>
                <p className="mt-3 flex-1 text-[13px] text-[var(--color-muted)]">{pkg.description}</p>
                <Link to="/sponsor" className="btn btn-ghost mt-4 w-full">
                  Book it
                </Link>
              </article>
            ))}
          </div>
        </Section>
      ) : null}

      <Section id="contact">
        <div className="card bg-gradient-to-br from-[var(--color-fairway-500)] to-[var(--color-fairway-800)] p-8">
          <h2 className="board-title text-[26px] font-bold">Series, tour or multi-club sponsorship</h2>
          <p className="mt-2 max-w-[58ch] text-sm text-[var(--color-sand)]">
            Title sponsorship of an event series, white-label boards or a fixed presence across every club in a
            province are quoted directly. Self-serve covers everything up to that.
          </p>
          <Link to="/sponsor" className="btn btn-primary mt-5">
            Open the sponsor portal
          </Link>
        </div>
      </Section>
    </AdProvider>
  );
}

function Fact({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="card p-4">
      <p className="eyebrow">{label}</p>
      <p className="tabular mt-1 text-lg font-bold text-[var(--color-fairway-300)]">{value}</p>
      <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-muted)]">{note}</p>
    </div>
  );
}

function PreviewBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="eyebrow mb-2">{title}</p>
      {children}
    </div>
  );
}
